import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const IconCovers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconReso = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
  </svg>
);
const IconSeated = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M3 12h18M8 6v12M16 6v12M3 18h18" />
  </svg>
);
const IconUpcoming = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
  </svg>
);

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}
function addMins(timeStr, mins) {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + mins;
  return `${String(Math.floor(total/60)%24).padStart(2,'0')}:${String(total%60).padStart(2,'0')}`;
}

const STATUS = {
  confirmed: { border: '#6366f1', dot: '#10b981', label: 'Confirmed', text: '#15803d', bg: 'rgba(16,185,129,0.08)' },
  pending:   { border: '#e2e8f0', dot: '#f59e0b', label: 'Pending',   text: '#92400e', bg: 'rgba(245,158,11,0.08)' },
  cancelled: { border: '#e2e8f0', dot: '#94a3b8', label: 'Cancelled', text: '#64748b', bg: 'rgba(148,163,184,0.08)' },
};

export default function TodayView() {
  const today = new Date().toISOString().slice(0, 10);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);

  const load = useCallback(async () => {
    try { setReservations(await api.getReservations({ date: today })); }
    catch (_) {} finally { setLoading(false); }
  }, [today]);

  useEffect(() => {
    load();
    window.addEventListener('seva:refresh', load);
    return () => window.removeEventListener('seva:refresh', load);
  }, [load]);

  const active      = reservations.filter(r => r.status !== 'cancelled');
  const now         = new Date();
  const nowStr      = String(now.getHours()).padStart(2,'0')+':'+String(now.getMinutes()).padStart(2,'0');
  const totalCovers = active.reduce((s, r) => s + r.party_size, 0);
  const seated      = active.filter(r => r.time_slot <= nowStr && addMins(r.time_slot,120) >= nowStr).length;
  const upcoming    = active.filter(r => r.time_slot > nowStr && r.time_slot <= addMins(nowStr,120)).length;

  const byTime = {};
  for (const r of active) { if (!byTime[r.time_slot]) byTime[r.time_slot]=[]; byTime[r.time_slot].push(r); }
  const slots = Object.keys(byTime).sort();
  const dayLabel = new Date(today+'T12:00:00Z').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',timeZone:'UTC'});

  if (loading && reservations.length === 0) return <div style={s.loading}>Loading…</div>;

  return (
    <div>
      <h1 style={s.h1}>Today's Overview</h1>
      <p style={s.sub}>{dayLabel}</p>

      <div style={s.stats}>
        {[
          { Icon: IconCovers,   value:totalCovers,   label:'Total covers'  },
          { Icon: IconReso,     value:active.length, label:'Reservations'  },
          { Icon: IconSeated,   value:seated,        label:'Seated now'    },
          { Icon: IconUpcoming, value:upcoming,      label:'Next 2 hours'  },
        ].map(({ Icon, value, label }) => (
          <div key={label} style={s.statCard}>
            <div style={s.statIcon}><Icon /></div>
            <div style={s.statValue}>{value}</div>
            <div style={s.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {slots.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            </svg>
          </div>
          <div style={s.emptyText}>No reservations today</div>
        </div>
      ) : (
        <div style={s.section}>
          <div style={s.sectionHeader}><h2 style={s.sectionTitle}>Timeline</h2></div>
          <div style={s.timeline}>
            {slots.map((slot, idx) => (
              <div key={slot} style={s.timeRow}>
                <div style={s.timeCol}>
                  <div style={s.timeText}>{formatTime(slot)}</div>
                  {idx < slots.length-1 && <div style={s.timeLine} />}
                </div>
                <div style={s.cards}>
                  {byTime[slot].map(r => {
                    const st = STATUS[r.status] || STATUS.confirmed;
                    return (
                      <div key={r.id} onClick={() => setSelected(r)}
                        style={{ ...s.resCard, borderLeft: `3px solid ${st.border}` }}
                        className="glass-card-hover t">
                        <div style={s.resCardTop}>
                          <span style={s.resName}>{r.customer_name}</span>
                          <span style={{ ...s.resBadge, color: st.text, background: st.bg }}>
                            <span style={{ ...s.resDot, background: st.dot }} />{st.label}
                          </span>
                        </div>
                        <div style={s.resMeta}>
                          <span style={s.metaChip}>{r.party_size} guests</span>
                          <span style={s.metaChip}>
                            {r.combined_tables && JSON.parse(r.combined_tables).length > 1 ? (r.tables_display || r.table_label) : r.table_label}
                          </span>
                          {r.source==='ai' && <span style={s.aiChip}>AI</span>}
                          {r.special_occasion && <span style={s.occasionChip}>🎉 {r.special_occasion}</span>}
                        </div>
                        {r.special_requests && <div style={s.resNote}>{r.special_requests}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTop}>
              <div>
                <h2 style={s.modalName}>{selected.customer_name}</h2>
                <div style={s.modalTime}>{formatTime(selected.time_slot)} · {selected.party_size} guests · {selected.table_label}</div>
              </div>
              <button style={s.closeBtn} onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={s.divider} />
            <dl style={s.grid}>
              {[['Confirmation',selected.confirmation_number],['Phone',selected.phone],['Email',selected.email||'—'],
                ['Status',selected.status],['Source',selected.source],['Dietary',selected.dietary_restrictions||'—'],
                ['Requests',selected.special_requests||'—'],['Notes',selected.notes||'—']]
              .map(([k,v]) => (
                <React.Fragment key={k}>
                  <dt style={s.dt}>{k}</dt><dd style={s.dd}>{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  loading:     { color:'var(--text-muted)', padding:40, fontSize:15 },
  h1:          { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:0, letterSpacing:'-0.5px', marginBottom:4 },
  sub:         { fontSize:14, color:'var(--text-muted)', marginBottom:28 },
  stats:       { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:28 },
  statCard:    { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:18, padding:'20px 22px', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  statIcon:    { width:38, height:38, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14, background:'#f1f5f9', color:'#475569' },
  statValue:   { fontSize:32, fontWeight:800, lineHeight:1, marginBottom:5, color:'#0f172a' },
  statLabel:   { fontSize:12, color:'#94a3b8', fontWeight:500 },
  section:     { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:20, overflow:'hidden', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  sectionHeader:{ padding:'16px 22px', background:'var(--section-bg)', borderBottom:'1px solid var(--section-border)' },
  sectionTitle:{ fontSize:11, fontWeight:700, color:'var(--text-muted)', margin:0, textTransform:'uppercase', letterSpacing:'0.08em' },
  timeline:    { padding:'4px 22px 22px' },
  timeRow:     { display:'flex', gap:20, paddingTop:18 },
  timeCol:     { width:74, flexShrink:0, display:'flex', flexDirection:'column', alignItems:'flex-end', paddingTop:8 },
  timeText:    { fontSize:12, fontWeight:700, color:'var(--text-muted)', whiteSpace:'nowrap' },
  timeLine:    { width:1, background:'var(--time-line)', flex:1, marginTop:8 },
  cards:       { flex:1, display:'flex', flexWrap:'wrap', gap:10 },
  resCard:     { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:14, padding:'13px 15px', cursor:'pointer', minWidth:200, flex:'0 0 auto' },
  resCardTop:  { display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:8 },
  resName:     { fontWeight:700, fontSize:14, color:'var(--text-primary)' },
  resBadge:    { fontSize:11, fontWeight:600, borderRadius:20, padding:'3px 8px', display:'flex', alignItems:'center', gap:4 },
  resDot:      { width:5, height:5, borderRadius:'50%', flexShrink:0 },
  resMeta:     { display:'flex', gap:6, flexWrap:'wrap' },
  metaChip:    { background:'var(--chip-bg)', color:'var(--chip-text)', borderRadius:6, padding:'3px 8px', fontSize:11, fontWeight:500 },
  aiChip:      { background:'rgba(79,70,229,0.08)', color:'#4f46e5', borderRadius:6, padding:'3px 8px', fontSize:11, fontWeight:700 },
  occasionChip:{ background:'rgba(251,191,36,0.12)', color:'#b45309', borderRadius:6, padding:'3px 8px', fontSize:11, fontWeight:700 },
  resNote:     { fontSize:12, color:'var(--text-muted)', marginTop:7 },
  empty:       { textAlign:'center', padding:'80px 0' },
  emptyIcon:   { fontSize:40, marginBottom:12 },
  emptyText:   { fontSize:15, color:'var(--text-muted)' },
  overlay:     { position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 },
  modal:       { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:22, padding:30, width:500, maxWidth:'90vw', maxHeight:'85vh', overflow:'auto', boxShadow:'0 20px 60px rgba(15,23,42,0.18)' },
  modalTop:    { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 },
  modalName:   { fontSize:20, fontWeight:800, color:'var(--text-primary)', margin:'0 0 4px' },
  modalTime:   { fontSize:13, color:'var(--text-muted)' },
  closeBtn:    { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, width:32, height:32, fontSize:20, cursor:'pointer', color:'#64748b', flexShrink:0 },
  divider:     { height:1, background:'#f1f5f9', margin:'0 0 18px' },
  grid:        { display:'grid', gridTemplateColumns:'120px 1fr', gap:'11px 14px' },
  dt:          { fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' },
  dd:          { fontSize:13, color:'var(--text-secondary)' },
};
